// src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";

import Sidebar from "./components/Sidebar";

import Home from "./pages/public/Home";
import Dashboard from "./pages/app/Dashboard";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Login from "./pages/public/Login";
import PunchClock from "./pages/app/Punch";
import PublicApplicationForm from "./pages/PublicApplicationForm";
import TournamentDates from "./pages/app/TournamentDates";
import SystemControls from "./pages/app/SystemControls";
import ApplicationSubmissions from "./pages/app/ApplicationSubmissions";
import EmployeeList from "./pages/app/EmployeeList";
import ScheduleHome from "./pages/app/ScheduleHome";
import TimeOffRequests from "./pages/app/TimeOffRequests";
import ShiftRequests from "./pages/app/ShiftRequests";

import PayrollHome from "./pages/app/PayrollHome";
import PayrollSummaryPage from "./pages/app/PayrollSummaryPage";
import CommandLoginPage from "./pages/CommandLoginPage";

import WhatToExpectPage from "./pages/WhatToExpectPage";
import OperationsPage from "./pages/OperationsPage";
import PolicyPage from "./pages/PolicyPage";

import { FEATURES } from "./config/features";

import AdminSchedule from "./pages/Schedule/AdminSchedule";
import SupervisorSchedule from "./pages/Schedule/SupervisorSchedule";
import DailySchedule from "./pages/Schedule/DailySchedule";
import EmployeeWeeklyView from "./pages/Schedule/EmployeeWeeklyView";
import Dispatch from "./pages/app/Dispatch";
import HQ from "./pages/app/HQ";
import PayRates from "./pages/app/PayRates";
import PermissionsPage from "./pages/PermissionsPage";
import Timecard from "./pages/app/Timecard";
import AdminPortalHome from "./pages/app/AdminPortalHome";
import EditPunches from "./pages/app/EditPunches";
import TimecardsAdmin from "./pages/app/TimecardsAdmin";
import CommandPage from "./pages/app/CommandPage";
import useUserRole, { normalizeRole } from "./hooks/useUserRole";
import useRolePermissions from "./hooks/useRolePermissions";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ViewModeProvider } from "./context/ViewModeContext";
import EmployeesHome from "./pages/app/EmployeesHome";

import HQPage from "./pages/HQPage";
import PatientListPage from "./pages/patients/PatientListPage";
import PatientChartPage from "./pages/patients/PatientChartPage";
import IncidentReportPage from "./pages/app/reports/IncidentReportPage";
import FieldAccessPage from "./pages/FieldAccessPage";
import ResponderLoginPage from "./pages/ResponderLoginPage";
import HQLoginPage from "./pages/HQLoginPage";
import DispatchLoginPage from "./pages/DispatchLoginPage";
import HQDashboardPage from "./pages/HQDashboardPage";
import DispatchDashboardPage from "./pages/DispatchDashboardPage";
import ResponderPage from "./pages/ResponderPage";
import StatusBoard from "./pages/app/StatusBoard";

import AssetManagementPage from "./pages/AssetManagement/AssetManagementPage";

import ResponderInventoryPage from "./pages/ResponderInventoryPage";
import ResponderSupplyRequestPage from "./pages/ResponderSupplyRequestPage";
import ResponderPCRPage from "./pages/app/reports/ResponderPCRPage";


import "./App.css";

const isRealUser = (u) => !!u && u.isAnonymous !== true;

function RequireAuth() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 16 }}>Loading auth…</div>;
  if (!isRealUser(user)) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function PortalLayout() {
  const location = useLocation();
  const hideSidebar = location.pathname.startsWith("/admin");
  const isAdminRoute = hideSidebar;

  return (
    <div className={`app-layout ${hideSidebar ? "app-layout--noSidebar" : ""}`}>
      {!hideSidebar && <Sidebar />}

      <div className="main-content">
        {isAdminRoute ? (
          <div className="adminContent">
            <Outlet />
          </div>
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  );
}

function RoleErrorPanel({ title = "Access", role, source }) {
  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      <p style={{ color: "#64748b" }}>
        Role could not be verified for this page, so the app is stopping here
        instead of redirecting.
      </p>
      <div
        style={{
          marginTop: 10,
          padding: 12,
          border: "1px solid #e5e7eb",
          borderRadius: 12,
        }}
      >
        <div>
          <strong>Raw role:</strong> {String(role || "null")}
        </div>
        <div>
          <strong>Normalized:</strong> {normalizeRole(role) || "null"}
        </div>
        <div>
          <strong>Source:</strong> {source}
        </div>
      </div>
      <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button className="btn" onClick={() => window.location.reload()}>
          Reload
        </button>
        <button
          className="btn btn-ghost"
          onClick={() => window.location.assign("/dashboard")}
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
}

function PermissionGuard({ permission, children, title = "Access" }) {
  const { role, loading: roleLoading, source } = useUserRole();
  const { can, loading: permsLoading } = useRolePermissions(role);

  if (roleLoading || permsLoading) {
    return <div style={{ padding: 16 }}>Loading permissions…</div>;
  }

  if (!role) {
    return <RoleErrorPanel title={title} role={role} source={source} />;
  }

  if (!can(permission)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function RoleScheduleRouter() {
  const { role, loading, source } = useUserRole();

  if (loading) return <div style={{ padding: 16 }}>Loading role for schedule…</div>;

  const r = normalizeRole(role);

  if (!r) return <RoleErrorPanel title="Schedule" role={role} source={source} />;

  if (r === "admin" || r === "super user" || r === "super admin") {
    return <AdminSchedule />;
  }

  if (r === "supervisor" || r === "manager") {
    return <SupervisorSchedule />;
  }

  return <Navigate to="/dashboard" replace />;
}

function RoleAwareRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (!isRealUser(user)) return <Navigate to="/login" replace />;
  return <Navigate to="/dashboard" replace />;
}

function PublicOnlyHome() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (isRealUser(user)) return <RoleAwareRedirect />;
  return <Home />;
}

function SupervisorViewOnlyBanner({ children }) {
  const { role } = useUserRole();
  const isSupervisor = normalizeRole(role) === "supervisor";

  return (
    <>
      {isSupervisor && (
        <div
          style={{
            padding: 12,
            margin: "12px 12px 0",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            color: "#0f172a",
          }}
        >
          <strong>View-only:</strong> Supervisors can view this page but cannot
          make changes.
        </div>
      )}
      {children}
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <ViewModeProvider>
          <Routes>
            <Route path="/" element={<PublicOnlyHome />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/what-to-expect" element={<WhatToExpectPage />} />
            <Route path="/operations" element={<OperationsPage />} />
            <Route path="/policies" element={<PolicyPage />} />
            <Route path="/apply" element={<PublicApplicationForm />} />
            <Route path="/login" element={<Login />} />

            <Route path="/field-access" element={<FieldAccessPage />} />
            <Route path="/field-access/responder" element={<ResponderLoginPage />} />
            <Route path="/field-access/command" element={<CommandLoginPage />} />
            <Route path="/field-access/hq/*" element={<HQLoginPage />} />
            <Route path="/field-access/dispatch" element={<DispatchLoginPage />} />
            <Route path="/field-access/status-board" element={<StatusBoard />} />
            <Route path="/patients" element={<PatientListPage />} />
            <Route path="/patients/:patientId" element={<PatientChartPage />} />
            <Route path="/responder/:unitId" element={<ResponderPage />} />    
            <Route path="/reports/incident" element={<IncidentReportPage />} />
            <Route path="/reports/pcr" element={<ResponderPCRPage />} />
            <Route path="/responder/:unitId/inventory" element={<ResponderInventoryPage />} />
            <Route path="/responder/:unitId/request-supplies" element={<ResponderSupplyRequestPage />} />

            <Route element={<RequireAuth />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/status-board" element={<StatusBoard />} />

              <Route element={<PortalLayout />}>
                <Route path="/punch" element={<PunchClock />} />
                <Route path="/timecard" element={<Timecard />} />
                <Route path="/admin" element={<Outlet />}>
                  <Route index element={<AdminPortalHome />} />
                  <Route path="status-board" element={<StatusBoard />} />

                  <Route path="/admin/dispatch" element={<Dispatch />} />
                  <Route path="/admin/hq/*" element={<HQPage />} />

                  <Route
                  path="payroll"
                  element={
                    <PermissionGuard permission="TimeCardsRead" title="Payroll">
                      <PayrollHome />
                    </PermissionGuard>
                  }
                />

                <Route
                  path="payroll/summary"
                  element={
                    <PermissionGuard permission="TimeCardsRead" title="Payroll Summary">
                      <PayrollSummaryPage />
                    </PermissionGuard>
                  }
                />

                <Route
  path="/admin/assets"
  element={
    <PermissionGuard
      permission="AssetManagement"
      title="Asset Management"
    >
      <AssetManagementPage />
    </PermissionGuard>
  }
/>

                  <Route
                  path="schedule"
                  element={
                    <PermissionGuard permission="FullScheduleRead" title="Schedule">
                      <ScheduleHome />
                    </PermissionGuard>
                  }
                />

                        <Route
                          path="schedule/full"
                          element={
                            <PermissionGuard permission="FullScheduleRead" title="Full Schedule">
                              <RoleScheduleRouter />
                            </PermissionGuard>
                          }
                        />

                        <Route
                          path="schedule/shift-requests"
                          element={
                            <PermissionGuard permission="FullScheduleRead" title="Shift Requests">
                              <ShiftRequests />
                            </PermissionGuard>
                          }
                        />

                        <Route
                          path="schedule/employees"
                          element={
                            <PermissionGuard
                              permission="FullScheduleRead"
                              title="Employee Weekly View"
                            >
                              <SupervisorViewOnlyBanner>
                                <EmployeeWeeklyView />
                              </SupervisorViewOnlyBanner>
                            </PermissionGuard>
                          }
                        />

                        <Route
                          path="daily-schedule"
                          element={
                            <PermissionGuard permission="DailyOps" title="Daily Operations">
                              <DailySchedule />
                            </PermissionGuard>
                          }
                        />

                        <Route
                        path="schedule/time-off"
                        element={
                          <PermissionGuard permission="FullScheduleRead" title="Time Off Requests">
                            <TimeOffRequests />
                          </PermissionGuard>
                        }
                      />

                      <Route
                      path="command"
                      element={
                        <PermissionGuard permission="DailyOps" title="Command">
                          <CommandPage />
                        </PermissionGuard>
                      }
                    />

                  <Route
                    path="tournament-dates"
                    element={
                      <PermissionGuard
                        permission="TournamentDates"
                        title="Tournament Dates"
                      >
                        <TournamentDates />
                      </PermissionGuard>
                    }
                  />

                  <Route
                    path="system-controls"
                    element={
                      <PermissionGuard
                        permission="ScheduleControl"
                        title="System Controls"
                      >
                        <SystemControls />
                      </PermissionGuard>
                    }
                  />

                  <Route
                    path="system/pay-rates"
                    element={
                      <PermissionGuard permission="PayRates" title="Set Pay Rates">
                        <PayRates />
                      </PermissionGuard>
                    }
                  />

                  <Route
                    path="applications"
                    element={
                      <PermissionGuard
                        permission="ApplicationsRead"
                        title="Applications"
                      >
                        <SupervisorViewOnlyBanner>
                          <ApplicationSubmissions />
                        </SupervisorViewOnlyBanner>
                      </PermissionGuard>
                    }
                  />

                  <Route
  path="employees"
  element={
    <PermissionGuard permission="EmployeesRead" title="Employees">
      <EmployeesHome />
    </PermissionGuard>
  }
/>

<Route
  path="employee-list"
  element={
    <PermissionGuard permission="EmployeesRead" title="Employee List">
      <SupervisorViewOnlyBanner>
        <EmployeeList />
      </SupervisorViewOnlyBanner>
    </PermissionGuard>
  }
/>

                  <Route
                    path="edit-punches"
                    element={
                      <PermissionGuard permission="EditPunches" title="Edit Punches">
                        <EditPunches />
                      </PermissionGuard>
                    }
                  />

                  <Route
                    path="timecards"
                    element={
                      <PermissionGuard permission="TimeCardsRead" title="Time Cards">
                        <TimecardsAdmin />
                      </PermissionGuard>
                    }
                  />

                  <Route
                    path="permissions"
                    element={
                      <PermissionGuard
                        permission="PermissionsEdit"
                        title="Permissions"
                      >
                        <PermissionsPage />
                      </PermissionGuard>
                    }
                  />
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<WildcardRedirect />} />
          </Routes>
        </ViewModeProvider>
      </AuthProvider>
    </Router>
  );
}

function WildcardRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (isRealUser(user)) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/" replace />;
}